import { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { CalculatorCard, SliderInput, RiskSelector } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export const CompoundCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(30000);
  const [monthlyContribution, setMonthlyContribution] = useState(3000);
  const [investmentYears, setInvestmentYears] = useState(20);
  const [riskProfile, setRiskProfile] = useState(8);
  const [expectedValue, setExpectedValue] = useState(0);
  const [optimisticValue, setOptimisticValue] = useState(0);
  const [pessimisticValue, setPessimisticValue] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    // Risk profile to return mapping (approximate)
    const baseReturn = 2 + (riskProfile * 0.8); // 2.8% to 10%
    const optimisticReturn = baseReturn + 3;
    const pessimisticReturn = Math.max(1, baseReturn - 3);

    const calculateFutureValue = (rate) => {
      const monthlyRate = rate / 100 / 12;
      let balance = initialInvestment;
      for (let i = 0; i < investmentYears * 12; i++) {
        balance = balance * (1 + monthlyRate) + monthlyContribution;
      }
      return Math.round(balance);
    };

    const expected = calculateFutureValue(baseReturn);
    const optimistic = calculateFutureValue(optimisticReturn);
    const pessimistic = calculateFutureValue(pessimisticReturn);

    setExpectedValue(expected);
    setOptimisticValue(optimistic);
    setPessimisticValue(pessimistic);

    // Generate chart data
    const data = [];
    for (let year = 0; year <= investmentYears; year++) {
      const months = year * 12;
      let balanceExpected = initialInvestment;
      let balanceOptimistic = initialInvestment;
      let balancePessimistic = initialInvestment;
      let totalDeposited = initialInvestment;

      for (let i = 0; i < months; i++) {
        balanceExpected = balanceExpected * (1 + baseReturn / 100 / 12) + monthlyContribution;
        balanceOptimistic = balanceOptimistic * (1 + optimisticReturn / 100 / 12) + monthlyContribution;
        balancePessimistic = balancePessimistic * (1 + pessimisticReturn / 100 / 12) + monthlyContribution;
        totalDeposited += monthlyContribution;
      }

      data.push({
        year: year,
        vklad: Math.round(totalDeposited),
        ocekavana: Math.round(balanceExpected),
        optimisticka: Math.round(balanceOptimistic),
        pesimisticka: Math.round(balancePessimistic)
      });
    }

    setChartData(data);
  }, [initialInvestment, monthlyContribution, investmentYears, riskProfile]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold mb-2">{label}. rok</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value?.toLocaleString('cs-CZ')} Kč
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const result = (
    <div className="space-y-4">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Očekávaná hodnota na konci investování</p>
        <p className="text-4xl font-bold text-green">{expectedValue.toLocaleString('cs-CZ')} Kč</p>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorOcekavana" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05}/>
              </linearGradient>
              <linearGradient id="colorVklad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#94A3B8" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="optimisticka" 
              name="Optimistický scénář"
              stroke="#FDE047" 
              fill="transparent"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Area 
              type="monotone" 
              dataKey="ocekavana" 
              name="Očekávaná hodnota"
              stroke="#22C55E" 
              fill="url(#colorOcekavana)" 
              strokeWidth={2}
            />
            <Area 
              type="monotone" 
              dataKey="pesimisticka" 
              name="Pesimistický scénář"
              stroke="#A78BFA" 
              fill="transparent"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Area 
              type="monotone" 
              dataKey="vklad" 
              name="Vklad"
              stroke="#94A3B8" 
              fill="url(#colorVklad)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 justify-center text-sm">
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-yellow-300"></div>
          <span className="text-gray-400">Optimistický scénář</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-green"></div>
          <span className="text-gray-400">Očekávaná hodnota</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-purple-400"></div>
          <span className="text-gray-400">Pesimistický scénář</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="legend-dot bg-gray-400"></div>
          <span className="text-gray-400">Vklad</span>
        </div>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="investice"
      title="Investiční kalkulačka"
      subtitle="Spočítejte si budoucí hodnotu investic"
      icon={TrendingUp}
      tip="Čím dříve začnete investovat, tím více pro vás pracuje složené úročení. Pravidelnost je klíčem k úspěchu - i malá částka měsíčně může za 20 let vytvořit velké bohatství."
      result={result}
      showResult={true}
      testId="compound-calculator"
      defaultOpen={true}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-gray-400 text-sm font-medium">Jednorázová investice</label>
          <div className="relative">
            <input
              type="number"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(Number(e.target.value) || 0)}
              className="number-input"
              data-testid="compound-initial-input"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">Kč</span>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-gray-400 text-sm font-medium">Měsíční investice</label>
          <div className="relative">
            <input
              type="number"
              value={monthlyContribution}
              onChange={(e) => setMonthlyContribution(Number(e.target.value) || 0)}
              className="number-input"
              data-testid="compound-monthly-input"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">Kč</span>
          </div>
        </div>

        <SliderInput
          label="Plánovaná délka investování"
          value={investmentYears}
          onChange={setInvestmentYears}
          min={1}
          max={40}
          step={1}
          suffix="let"
          formatValue={(v) => `${v} let`}
          testId="compound-years-slider"
        />

        <RiskSelector
          value={riskProfile}
          onChange={setRiskProfile}
          testId="risk-profile"
        />
      </div>
    </CalculatorCard>
  );
};
